'use client';

/* eslint-disable @typescript-eslint/no-empty-function */
import { captureException } from '@sentry/nextjs';
import { kebabCase } from 'lodash';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  Key,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react';
import { searchProviders } from '@provider-directory/app/actions/search';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { getUrlSlug } from '@provider-directory/functions/get-slug';
import { useSearchAnalytics } from '@provider-directory/hooks/useSearchAnalytics';
import { withoutTrackingParams } from '@provider-directory/lib/withoutTrackingParams';
import { getSpecialtySlugFromPathname } from '@provider-directory/models/navigation.model';
import { LocationData } from '@provider-directory/types/locations.contracts';
import { ProviderSearchActionResponse } from '@provider-directory/types/providers';
import {
  DEFAULT_SEARCH_PARAMS,
  ProviderSortingEnum,
  ProvidersSearchCriteria,
} from '@provider-directory/types/providers.contracts';
import {
  PARAMS_TO_PRESERVE,
  ProvidersFacetName,
  hasNarrowingFilters,
  parseSearchParams,
  getEmptyStatePreservedParams,
} from '@provider-directory/types/search.model';

export type FilterKey =
  | ProvidersFacetName
  | 'address'
  | 'reasonForVisit'
  | 'providerGroup'
  | 'page';

interface FilterValue {
  key: FilterKey;
  option: Set<Key> | Key | LocationData | null;
}

const getSpecialtiesPath = (option: string | null): string => {
  let newPathname;

  if (option) {
    newPathname = `/providers/specialty/${getUrlSlug(option)}`;
  } else {
    newPathname = '/providers/find';
  }

  return newPathname;
};

const getLocationParams = (
  option: LocationData | null,
  searchParams: URLSearchParams,
  defaultMaxDistance: number,
): URLSearchParams => {
  const newParams = new URLSearchParams(searchParams);

  if (option) {
    newParams.set('address', option.value);
    newParams.set('center-point', `${option.lat},${option.lng}`);

    if (!newParams.has('max-distance')) {
      newParams.set('max-distance', String(defaultMaxDistance));
    }
  } else {
    newParams.delete('address');
    newParams.delete('center-point');
    newParams.delete('max-distance');
  }

  return newParams;
};

const getSearchResultsPath = (
  { key, option }: FilterValue,
  pathname: string,
  searchParams: URLSearchParams,
  defaultMaxDistance: number,
): [string, URLSearchParams] => {
  const newParams = new URLSearchParams(searchParams);
  const parsedParams = Object.fromEntries(newParams.entries());
  const keySlug = kebabCase(key);

  if (keySlug === 'specialties') {
    const specialtyPath = getSpecialtiesPath(option as string | null);
    newParams.delete('reason-for-visit');
    newParams.delete('provider-group');
    return [specialtyPath, newParams];
  } else if (keySlug === 'address') {
    if (
      parsedParams.sort === (ProviderSortingEnum.enum.distance as string) &&
      !option
    ) {
      newParams.set('sort', DEFAULT_SEARCH_PARAMS.sort!);
    }

    const locationParams = getLocationParams(
      option as LocationData | null,
      newParams,
      defaultMaxDistance,
    );
    return [pathname, locationParams];
  }

  if (option instanceof Set) {
    newParams.delete(keySlug);
    const options = Array.from(option);
    options.forEach((opt) => {
      newParams.append(keySlug, String(opt));
    });
  } else if (typeof option === 'string') {
    newParams.set(keySlug, option);
  } else {
    newParams.delete(keySlug);
  }

  return [pathname, newParams];
};

export type SpecialtyPresence =
  | 'unknown'
  | 'exists-in-subdirectory'
  | 'not-in-subdirectory';

export interface SearchContextType {
  loadingResults: boolean;
  searchCriteria: ProvidersSearchCriteria;
  filters?: ProviderSearchActionResponse['form'];
  pagination?: ProviderSearchActionResponse['pagination'];
  providers?: ProviderSearchActionResponse['providers'];
  specialtyNames?: string[];
  specialtyPresence: SpecialtyPresence;
  isLocationSelected: boolean;
  search: (newOption: {
    key: FilterKey;
    option: Set<Key> | Key | LocationData | null;
  }) => void;
  clear: () => void;
  clearFiltersKeepScope: () => void;
}

export const emptySearchContext: SearchContextType = {
  loadingResults: true,
  searchCriteria: {} as ProvidersSearchCriteria,
  specialtyPresence: 'unknown',
  isLocationSelected: false,
  search: () => {},
  clear: () => {},
  clearFiltersKeepScope: () => {},
};

export const SearchContext =
  createContext<SearchContextType>(emptySearchContext);

// Normalises a criteria value to an array so scalar and array shapes of the
// same facet (e.g. `languages`) can be diffed with the same logic.
const toComparableArray = (value: unknown): unknown[] =>
  value === undefined || value === null
    ? []
    : Array.isArray(value)
      ? value
      : [value];

// Builds comma-separated `filter` and `value` strings describing
// which search criteria changed between requests, for analytics.
// Undefined/null values are skipped so they do not throw or serialize
// as the literal strings "undefined"/"null".
export const getFilterChangeFields = (
  current: ProvidersSearchCriteria,
  previous?: ProvidersSearchCriteria,
): { filter: string; value: string } => {
  if (!previous) {
    return Object.entries(current).reduce(
      (acc, [key, value]) => ({
        filter: [acc.filter, key].filter(Boolean).join(','),
        value: [acc.value, value?.toString()].filter(Boolean).join(','),
      }),
      { filter: '', value: '' },
    );
  }
  return Object.entries(current).reduce(
    (acc, [key, value]) => {
      const previousValue = previous[key as keyof ProvidersSearchCriteria];
      // Facets such as `languages`/`ageGroups` can arrive as either a scalar
      // (one URL param occurrence) or an array (repeated occurrences), so
      // `value` and `previousValue` don't always agree on which shape they
      // are. Normalise both to arrays whenever either side is one, instead
      // of assuming the shape of `previousValue` from `value` alone.
      const isDiff =
        Array.isArray(value) || Array.isArray(previousValue)
          ? toComparableArray(value).sort().toString() !==
            toComparableArray(previousValue).sort().toString()
          : previousValue !== value;
      if (isDiff) {
        return {
          filter: [acc.filter, key].filter(Boolean).join(','),
          value: [acc.value, value?.toString()].filter(Boolean).join(','),
        };
      }
      return acc;
    },
    { filter: '', value: '' },
  );
};

export const SearchContextProvider = ({
  children,
}: {
  children: ReactElement;
}): ReactElement => {
  const router = useRouter();
  const analytics = useAnalytics();
  const searchParams = useSearchParams();
  const [searchParamsHistory, setSearchParamsHistory] = useState<
    ProvidersSearchCriteria[]
  >([]);
  const { sendFilterUpdateAnalytics } = useSearchAnalytics();
  const searchCriteria = parseSearchParams(searchParams);
  const pathname = usePathname();
  const slug = getSpecialtySlugFromPathname(pathname);

  const [loadingResults, setLoadingResults] = useState(true);

  const {
    config: { defaultMaxDistance },
  } = useOrgContext();

  const [providersResponse, setProvidersResponse] =
    useState<ProviderSearchActionResponse | null>();

  // Tracks whether the specialty exists in this subdirectory.
  // - 'unknown': no unfiltered response received yet
  // - 'not-in-subdirectory': unfiltered response had 0 results
  // - 'exists-in-subdirectory': any response had results
  const [specialtyPresence, setSpecialtyPresence] =
    useState<SpecialtyPresence>('unknown');

  // Ref mirrors specialtyPresence so the loadProviders effect can read
  // the latest value without adding it to its dependency array.
  const specialtyPresenceRef = useRef<SpecialtyPresence>('unknown');
  useEffect(() => {
    specialtyPresenceRef.current = specialtyPresence;
  }, [specialtyPresence]);

  // Avoid stale data when transitioning between specialties
  useEffect(() => {
    setProvidersResponse(null);
    setSpecialtyPresence('unknown');
  }, [slug]);

  // URL PARAMATER UPDATE FUNCTIONS
  const clear = () => {
    const newParams = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
      if (PARAMS_TO_PRESERVE.includes(key)) {
        if (
          key === 'sort' &&
          value === (ProviderSortingEnum.enum.distance as string)
        ) {
          newParams.set(key, DEFAULT_SEARCH_PARAMS.sort!);
        } else {
          newParams.set(key, value);
        }
      }
    }

    const newPath = `/providers/find?${newParams.toString()}`;

    // Don't trigger loading state if the URL won't change (already cleared)
    if (
      pathname === '/providers/find' &&
      newParams.toString() === searchParams.toString()
    ) {
      return;
    }

    setLoadingResults(true);
    setTimeout(() => {
      // Reset focus to main so the next Tab starts from the top of the page after client-side navigation
      document.getElementById('main-content')?.focus();
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 100);

    router.push(newPath, { scroll: false });
  };

  // Iterative: first press drops narrowing filters, keeping specialty + location;
  // once only those remain, the next press clears them too.
  const clearFiltersKeepScope = () => {
    const newParams = getEmptyStatePreservedParams(searchParams);
    const newQuery = newParams.toString();

    if (newQuery === searchParams.toString()) {
      clear();
      return;
    }

    setLoadingResults(true);
    setTimeout(() => {
      document.getElementById('main-content')?.focus();
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 100);

    const nextUrl = newQuery ? `${pathname}?${newQuery}` : pathname;
    router.push(nextUrl, { scroll: false });
  };

  const search = ({ key, option }: FilterValue) => {
    const newSearchParams = withoutTrackingParams(searchParams);
    if (newSearchParams.has('page')) {
      // new search means going back to page 1
      newSearchParams.set('page', '1');
    }

    const [newPathname, newParams] = getSearchResultsPath(
      { key, option },
      pathname,
      newSearchParams,
      defaultMaxDistance,
    );

    // We don't want to trigger search if the pathname is the same
    if (
      newPathname === pathname &&
      newParams.toString() === newSearchParams.toString()
    ) {
      return;
    }

    setLoadingResults(true);
    setTimeout(() => {
      // Reset focus to main so the next Tab starts from the top of the page after client-side navigation
      document.getElementById('main-content')?.focus();
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 100);
    sendFilterUpdateAnalytics(key, option);

    router.push(`${newPathname}?${newParams.toString()}`, { scroll: false });
  };

  // PARAMS THAT WATCH URL FOR CHANGES
  // Use the string representation of searchParams as the dependency
  // instead of the object reference. On iOS Safari, useSearchParams()
  // can return a new ReadonlyURLSearchParams reference on every render
  // even when the URL hasn't changed, which causes an infinite loop
  // when used as a useCallback dependency.
  const searchParamsString = searchParams.toString();

  const fetchProviders =
    useCallback(async (): Promise<ProviderSearchActionResponse> => {
      const stableSearchParams = new URLSearchParams(searchParamsString);
      const parsedSearchParams = parseSearchParams(stableSearchParams);
      const paramsWithSpecialty = {
        ...parsedSearchParams,
        specialties: slug ? [slug] : parsedSearchParams.specialties,
      };
      const response = await searchProviders({
        searchParams: parsedSearchParams,
        slug,
      });

      if (response.pagination.total === 0) {
        try {
          const diffFields = getFilterChangeFields(
            paramsWithSpecialty,
            searchParamsHistory[searchParamsHistory.length - 1],
          );
          analytics.track('RECEIVED_NO_RESULTS', {
            params: paramsWithSpecialty,
            ...diffFields,
            field: diffFields.filter,
            filterValues: diffFields.value,
          });
        } catch (error) {
          captureException(error);
        }
      }

      setSearchParamsHistory((prev) => [...prev, paramsWithSpecialty]);

      return response;
      // analytics can't be included here because it changes when mounted
      // due to its isMounted logic. It causes duplicate search requests.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, searchParamsString]);

  useEffect(() => {
    const loadProviders = async (): Promise<void> => {
      try {
        setLoadingResults(true);
        const response = await fetchProviders();
        setProvidersResponse(response);

        // Update specialty presence based on response.
        // Use searchParamsString (stable) to avoid closure/lint issues.
        const currentParams = new URLSearchParams(searchParamsString);
        if (response.pagination.total > 0) {
          setSpecialtyPresence('exists-in-subdirectory');
        } else if (!hasNarrowingFilters(currentParams)) {
          // Zero results with no narrowing filters means the specialty
          // truly doesn't exist in this subdirectory
          setSpecialtyPresence('not-in-subdirectory');
        } else if (slug && specialtyPresenceRef.current === 'unknown') {
          // Zero results WITH narrowing filters and presence still unknown:
          // make a secondary call without filters to determine whether
          // the specialty itself exists in this subdirectory.
          try {
            const unfilteredResponse = await searchProviders({
              searchParams: {} as ProvidersSearchCriteria,
              slug,
            });
            if (unfilteredResponse.pagination.total > 0) {
              setSpecialtyPresence('exists-in-subdirectory');
            } else {
              setSpecialtyPresence('not-in-subdirectory');
            }
          } catch (error) {
            captureException(error);
          }
        }
      } catch (error) {
        captureException(error);
      } finally {
        setLoadingResults(false);
      }
    };

    loadProviders();
    // searchParamsString is used inside loadProviders for specialtyPresence
    // but fetchProviders already depends on it, so no extra dep needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProviders]);

  return (
    <SearchContext.Provider
      value={{
        loadingResults,
        searchCriteria,
        filters: providersResponse?.form,
        pagination: providersResponse?.pagination,
        providers: providersResponse?.providers,
        specialtyNames: providersResponse?.specialtyNames,
        specialtyPresence,
        isLocationSelected:
          !!searchCriteria.address || !!searchCriteria.centerPoint,
        search,
        clear,
        clearFiltersKeepScope,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = (): SearchContextType => {
  return useContext(SearchContext);
};
